import { useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EMICalculator, EMICalculatorData } from '@/components/EMICalculator';
import { EMIResults } from '@/components/EMIResults';
import { LoanCharts } from '@/components/LoanCharts';
import { EMIFAQ, emiFAQs } from '@/components/faq/EMIFAQ';
import { Footer } from '@/components/Footer';
import { LoanSchedule } from '@/lib/loanCalculator';
import { motion } from 'framer-motion';
import { CalculatorTabs } from '@/components/CalculatorTabs';
import { useCalculatorTabs } from '@/hooks/useCalculatorTabs';
import { useGlobalHistory } from '@/hooks/useGlobalHistory';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useIsMobile } from '@/hooks/use-mobile';
import { Helmet } from 'react-helmet-async';
import Meta from '@/components/Meta';
import { CalcNotePromo } from '@/components/CalcNotePromo';
import { AdsterraNativeBanner } from '@/components/AdsterraNativeBanner';
import { RelatedCalculators } from '@/components/RelatedCalculators';
import { ShareCalculationButton } from '@/components/ShareCalculationButton';

const AmortizationTable = lazy(() => import('@/components/AmortizationTable').then(m => ({ default: m.AmortizationTable })));
const PrepaymentPlanner = lazy(() => import('@/components/PrepaymentPlanner').then(m => ({ default: m.PrepaymentPlanner })));
const EMIGuideSection = lazy(() => import('@/components/EMIGuideSection').then(m => ({ default: m.EMIGuideSection })));

 const defaultEMIData: EMICalculatorData = {
  principal: 2500000,
  rate: 8.5,
  tenureYears: 20,
  tenureMonths: 0,
  interestType: 'reducing',
  loanTypeId: 'personal',
  vehiclePrice: 1000000,
  downPayment: 200000,
  result: null,
};

const Index = ({canonical}:{canonical: string}) => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  // Read initial values from URL query parameters if present
  const initialData = useMemo(() => {
    const p = searchParams.get('p');
    const r = searchParams.get('r');
    const y = searchParams.get('y');
    const m = searchParams.get('m');
    const type = searchParams.get('type');
    const interest = searchParams.get('interest');

    return {
      ...defaultEMIData,
      principal: p ? Math.max(0, Number(p)) : defaultEMIData.principal,
      rate: r ? Math.max(0.1, Number(r)) : defaultEMIData.rate,
      tenureYears: y ? Math.max(0, Number(y)) : defaultEMIData.tenureYears,
      tenureMonths: m ? Math.max(0, Number(m)) : defaultEMIData.tenureMonths,
      loanTypeId: type || defaultEMIData.loanTypeId,
      interestType: interest === 'flat' ? 'flat' : 'reducing',
    };
  }, [searchParams]);

  const {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    addTab,
    removeTab,
    duplicateTab,
    renameTab,
    updateTabData
  } = useCalculatorTabs('emi', initialData as unknown as Record<string, unknown>);

  const { history, addToHistory: addHistoryEntry, deleteEntry, clearHistory } = useGlobalHistory();
  
  // Filter history to only show EMI calculator entries
  const emiHistory = history.filter(h => h.type === 'EMI');

  // Get current tab data with proper defaults
  const tabData: EMICalculatorData = {
    principal: (activeTab.data.principal as number) ?? defaultEMIData.principal,
    rate: (activeTab.data.rate as number) ?? defaultEMIData.rate,
    tenureYears: (activeTab.data.tenureYears as number) ?? defaultEMIData.tenureYears,
    tenureMonths: (activeTab.data.tenureMonths as number) ?? defaultEMIData.tenureMonths,
    interestType: (activeTab.data.interestType as 'reducing' | 'flat') ?? defaultEMIData.interestType,
    loanTypeId: (activeTab.data.loanTypeId as string) ?? defaultEMIData.loanTypeId,
    vehiclePrice: (activeTab.data.vehiclePrice as number) ?? defaultEMIData.vehiclePrice,
    downPayment: (activeTab.data.downPayment as number) ?? defaultEMIData.downPayment,
    result: (activeTab.data.result as LoanSchedule | null) ?? null,
  };

  const loanData = tabData.result;
  const principal = tabData.principal;
  const rate = tabData.rate;
  const tenureYears = tabData.tenureYears;
  const tenureMonths = tabData.tenureMonths;
  const totalTenureMonths = tenureYears * 12 + tenureMonths;

  // Track last history key to prevent duplicate entries
  const lastHistoryKeyRef = useRef<string>('');

  // Handle data changes from the calculator - updates activeTab.data
  const handleDataChange = useCallback((updates: Partial<EMICalculatorData>) => {
    updateTabData(activeTabId, updates as Record<string, unknown>);
  }, [activeTabId, updateTabData]);

  // Handle calculation result - stores result in tab data
  const handleCalculate = useCallback((result: LoanSchedule) => {
    updateTabData(activeTabId, { result } as Record<string, unknown>);
    
    // Create a unique key for this calculation to prevent duplicates
    const historyKey = `${principal}-${rate}-${tenureYears}-${tenureMonths}-${result.result.emi}`;
    
    // Only add to history if this is a different calculation
    if (historyKey !== lastHistoryKeyRef.current) {
      lastHistoryKeyRef.current = historyKey;
      
      addHistoryEntry({
        type: 'EMI',
        title: `₹${result.result.emi.toLocaleString('en-IN', { maximumFractionDigits: 0 })}/mo`,
        summary: `₹${principal.toLocaleString('en-IN')} @ ${rate}% for ${tenureYears}y ${tenureMonths}m`,
        data: {
          principal,
          rate,
          tenureYears,
          tenureMonths,
          emi: result.result.emi,
          totalInterest: result.result.totalInterest,
          totalPayment: result.result.totalPayable,
        }
      });
    }
  }, [activeTabId, updateTabData, addHistoryEntry, principal, rate, tenureYears, tenureMonths]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
        <Meta 
          canonical={canonical}
          tags='home loan emi calculator with prepayment, car loan emi calculator with down payment, personal loan emi calculator, flat vs reducing rate calculator, amortization schedule excel pdf, loan emi calculator india, utiloom'
          title='EMI Calculator with Prepayment: Home & Car Loan EMI Planner' 
          desc='Calculate Equated Monthly Installments (EMI), loan interest savings with part-payments, compare flat vs reducing rates, and export full amortization schedules.'
          faqItems={emiFAQs}
        />
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 lg:px-8 max-w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                EMI Calculator for Home, Car & Personal Loans
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                Calculate your exact monthly loan EMI, visualize principal versus interest breakdown, and plan prepayments to save lakhs in total interest outgo.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-center">
              <ShareCalculationButton
                title="EMI Loan Calculation"
                params={{
                  p: principal,
                  r: rate,
                  y: tenureYears,
                  m: tenureMonths,
                  type: tabData.loanTypeId,
                  interest: tabData.interestType,
                }}
              />
              {!isMobile && <ThemeToggle />}
            </div>
          </div>
        </motion.div>

        {/* Calculator Tabs */}
        <CalculatorTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={setActiveTabId}
          onAddTab={addTab}
          onRemoveTab={removeTab}
          onDuplicateTab={duplicateTab}
          onRenameTab={renameTab}
          history={emiHistory}
          onClearHistory={clearHistory}
          onDeleteHistoryEntry={deleteEntry}
        />
        
        {/* Calculator Section */}
        <section id="calculator" className="scroll-mt-20">
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12">
            {/* Left - Calculator */}
            <EMICalculator 
              data={tabData}
              onDataChange={handleDataChange}
              onCalculate={handleCalculate}
            />
            
            {/* Right - Results */}
            <div className="space-y-6">
              <EMIResults data={loanData} />
              <LoanCharts data={loanData} />
            </div>
          </div>
        </section>

        {/* Prepayment Planner */}
        <Suspense fallback={<div className="h-48 rounded-xl bg-card/40 animate-pulse my-6" />}>
          <section id="prepayment" className="scroll-mt-20 mb-12">
            <PrepaymentPlanner 
              loanData={loanData}
              principal={principal}
              rate={rate}
              tenureMonths={totalTenureMonths}
            />
          </section>

          {/* Amortization Table */}
          <section id="breakdown" className="scroll-mt-20 mb-12">
            <AmortizationTable data={loanData} />
          </section>
        </Suspense>

        {/* Adsterra Native Banner */}
        <AdsterraNativeBanner />

        {/* Educational & Comparison SEO Guide */}
        <Suspense fallback={<div className="h-48 rounded-xl bg-card/40 animate-pulse my-6" />}>
          <EMIGuideSection />
        </Suspense>

        {/* FAQ Section */}
        <section id="faq" className="scroll-mt-20 mb-12">
          <EMIFAQ />
        </section>

        {/* Related Calculators for Internal Link Equity */}
        <RelatedCalculators currentId="emi" />

        {/* CalcNote Mobile App Banner */}
        <CalcNotePromo variant="banner" />
      </div>

      {/* Floating Bottom App CTA on Mobile */}
      <CalcNotePromo variant="floating" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
